import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, CreditCard, Receipt, User, Users, Calendar, HeartHandshake,
  FileText, Bell, ShieldCheck, Vote, LogOut, Menu, Home, PanelLeftClose, PanelLeftOpen, HelpCircle, Newspaper } from "lucide-react";
import Emblem from "./Emblem";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { useLang } from "../i18n/LanguageContext";
import LanguageToggle from "./LanguageToggle";
import { useLive } from "./LiveFeed";
import { AvatarMenu } from "./AvatarMenu";
import { useSidebarCollapsed } from "../hooks/useSidebarCollapsed";
import { PageTip, HelpButton } from "./Tips";
import { VersionBadge } from "./About";
import { BottomTabBar } from "./BottomTabBar";

const ITEMS = [
  { to: "/portal", end: true, icon: LayoutDashboard, label: "Dashboard" },
  { to: "/portal/standing", icon: ShieldCheck, label: "Good Standing" },
  { to: "/portal/dues", icon: CreditCard, label: "Dues & Levies" },
  { to: "/portal/payments", icon: Receipt, label: "Payments" },
  { to: "/portal/events", icon: Calendar, label: "Events" },
  { to: "/portal/welfare", icon: HeartHandshake, label: "Welfare" },
  { to: "/portal/directory", icon: Users, label: "Directory" },
  { to: "/portal/documents", icon: FileText, label: "Documents" },
  { to: "/portal/newsletter", icon: Newspaper, label: "Newsletter" },
  { to: "/portal/elections", icon: Vote, label: "Elections" },
  { to: "/portal/notifications", icon: Bell, label: "Notifications" },
  { to: "/portal/profile", icon: User, label: "Profile" },
  { to: "/portal/help", icon: HelpCircle, label: "Help & Tips" },
];

const navCls = (mini) => ({ isActive }) => `relative flex items-center gap-3 rounded-lg py-2.5 text-sm transition-colors ${mini ? "justify-center px-0" : "px-3"} ${
  isActive ? "bg-gold text-crimson font-semibold" : "text-parchment/75 hover:bg-white/10"}`;

function SideNav({ mini, unread, onNavigate, t }) {
  return (
    <nav className="mt-2 flex-1 space-y-1 overflow-y-auto sidebar-scroll min-h-0 pr-1">
      {ITEMS.map((it) => (
        <NavLink key={it.to} to={it.to} end={it.end} onClick={onNavigate} title={mini ? t(it.label) : undefined}
          data-testid={`portal-nav-${it.label.toLowerCase().replace(/ /g, "-")}`} className={navCls(mini)}>
          <it.icon size={18} className="shrink-0" /> {!mini && t(it.label)}
          {it.label === "Notifications" && unread > 0 && (
            <span className={`rounded-full bg-crimson font-bold text-parchment ${mini ? "absolute -right-0.5 -top-0.5 h-4 min-w-4 px-1 text-[10px] leading-4" : "ml-auto px-2 py-0.5 text-xs"}`} data-testid="live-unread-badge">{unread}</span>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function SideFooter({ mini, connected, isOfficer, t }) {
  const nav = useNavigate();
  const { logout } = useAuth();
  const btn = `w-full gap-3 hover:bg-white/10 ${mini ? "justify-center px-0" : "justify-start"}`;
  return (
    <div className="space-y-2 border-t border-white/10 pt-3">
      {!mini && (
        <div className="flex items-center justify-between px-2 pb-1">
          <LanguageToggle dark />
          <VersionBadge />
          <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-parchment/50" data-testid="live-status">
            <span className={`h-2 w-2 rounded-full ${connected ? "bg-green-400 animate-pulse" : "bg-parchment/30"}`} />
            {connected ? t("Live") : t("Reconnecting…")}
          </span>
        </div>
      )}
      {isOfficer && <Button onClick={() => nav("/admin")} variant="ghost" title={t("Admin Area")} className={`${btn} text-palm`} data-testid="portal-to-admin"><ShieldCheck size={18} /> {!mini && t("Admin Area")}</Button>}
      <Button onClick={() => nav("/")} variant="ghost" title={t("Public Site")} className={`${btn} text-parchment/70`}><Home size={18} /> {!mini && t("Public Site")}</Button>
      <Button onClick={async () => { await logout(); nav("/"); }} variant="ghost" title={t("Sign out")} className={`${btn} text-parchment/70`} data-testid="portal-logout"><LogOut size={18} /> {!mini && t("Sign out")}</Button>
    </div>
  );
}

function SideContent({ mini = false, onNavigate }) {
  const { isOfficer } = useAuth();
  const { t } = useLang();
  const { unread, connected } = useLive();
  return (
    <>
      <Link to="/" className={`flex items-center gap-3 py-4 ${mini ? "justify-center px-0" : "px-2"}`} title="Okuapemman Association of Massachusetts"><Emblem size={40} />
        {!mini && <div><div className="font-serif-display text-base font-bold leading-tight text-gold">Okuapemman Association of Massachusetts</div>
        <div className="eyebrow text-parchment/50">{t("Member Portal")}</div></div>}
      </Link>
      <SideNav mini={mini} unread={unread} onNavigate={onNavigate} t={t} />
      <SideFooter mini={mini} connected={connected} isOfficer={isOfficer} t={t} />
    </>
  );
}

function PortalHeader({ collapsed, toggle, onOpen }) {
  const { user } = useAuth();
  const { t } = useLang();
  return (
    <header className="glass sticky top-0 z-30 flex items-center justify-between px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] lg:px-8">
      <div className="flex items-center gap-1 lg:hidden">
        <button className="flex h-11 w-11 items-center justify-center rounded-lg" aria-label="Open menu" onClick={onOpen} data-testid="portal-mobile-toggle"><Menu /></button>
        <Link to="/" className="flex h-11 w-11 items-center justify-center rounded-lg text-crimson/70" title={t("Public Site")} aria-label="Public site" data-testid="portal-mobile-home"><Home size={20} /></Link>
      </div>
      <button onClick={toggle} className="hidden rounded-lg p-2 text-crimson/70 transition-colors hover:bg-crimson/10 hover:text-crimson lg:block"
        title={collapsed ? t("Expand menu") : t("Collapse menu")} aria-label={collapsed ? "Expand menu" : "Collapse menu"} data-testid="portal-sidebar-toggle">
        {collapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
      </button>
      <div className="flex items-center gap-3">
        <HelpButton />
        <div className="text-right">
          <p className="text-sm font-semibold">{user?.name}</p>
          <p className="eyebrow text-gold">{user?.role}</p>
        </div>
        <AvatarMenu area="portal" />
      </div>
    </header>
  );
}

export default function PortalLayout() {
  const [open, setOpen] = useState(false);
  const { collapsed, toggle } = useSidebarCollapsed();
  const close = () => setOpen(false);
  return (
    <div className="flex min-h-screen bg-parchment">
      <aside className={`fixed hidden h-screen flex-col overflow-hidden bg-slate-950 transition-[width] duration-200 lg:flex ${collapsed ? "w-[72px] p-3" : "w-64 p-4"}`} data-testid="portal-sidebar" data-collapsed={collapsed}>
        <SideContent mini={collapsed} onNavigate={close} />
      </aside>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="portal-mobile-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={close} />
          <aside className="absolute left-0 top-0 flex h-full w-64 flex-col overflow-hidden bg-slate-950 p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]"><SideContent onNavigate={close} /></aside>
        </div>
      )}
      <div className={`min-w-0 flex-1 transition-[margin] duration-200 ${collapsed ? "lg:ml-[72px]" : "lg:ml-64"}`}>
        <PortalHeader collapsed={collapsed} toggle={toggle} onOpen={() => setOpen(true)} />
        <div className="p-4 pb-24 lg:p-8"><PageTip /><Outlet /></div>
      </div>
      <BottomTabBar />
    </div>
  );
}
